/**
 * Grid summaries: a table of a question with more than one instance (a grid,
 * a loop, or both) with one row per instance, each showing the same thing
 * (`SummaryChoice`). Which choices fit a question, how a choice is named,
 * and which instances are a summary's rows: those in its `slice` (a
 * question of several dimensions is summarised along one of them, the
 * others fixed), without its `hiddenRows`.
 */
import { CatalogueInstance, CatalogueQuestion } from "../types/catalogue"
import { SummaryChoice, TabSpecDefaults } from "../types/tabSpecDocument"
import { isNps } from "./scale"
import { RAW_NAMES, ShownNames } from "./shownLabels"

/**
 * Whether dimension `k` of `q` is the grid's own (its rows, or its columns
 * after them) and not a loop: told by where the catalogue put the grid rows
 * (`rowsDimension`), never by the name, which a loop's driver may share.
 */
const isGridDimension = (q: Pick<CatalogueQuestion, "rowsDimension">, k: number): boolean =>
  q.rowsDimension !== undefined && k >= q.rowsDimension

/** A number grid (a table of numbers) or a number asked in a loop: summarised by its mean or its sum. */
export const isNumberQuestion = (q: Pick<CatalogueQuestion, "type">): boolean =>
  q.type === "grid-number" || q.type === "number"

/**
 * Whether `q` can have a grid summary: it is not text, and it is a grid (of
 * any number of rows, as before) or has more than one instance (a loop).
 */
export const canSummarise = (q: Pick<CatalogueQuestion, "type" | "instances">): boolean =>
  q.type !== "text" && (q.type.startsWith("grid-") || q.instances.length > 1)

/**
 * What a summary table without its own `summary` shows, as it always did:
 * the mean of a number question or when the defaults' grid summary is the
 * mean, otherwise the top box of the spec's size (2 unless it is 3).
 */
export const legacySummary = (
  q: Pick<CatalogueQuestion, "type"> | null,
  d: TabSpecDefaults
): SummaryChoice =>
  (q && isNumberQuestion(q)) || d.grid.summary === "mean"
    ? { kind: "mean" }
    : { kind: "top", size: d.scale.topBox === 3 ? 3 : 2 }

/**
 * A summary choice as the titles and the lists name it: "Top 2", "Bottom
 * 3", "Middle", the answer as shown (`optionName`), the net's name, "Mean",
 * "NPS", "Sum".
 */
export const summaryName = (
  choice: SummaryChoice,
  optionName: (label: string) => string = (l) => l
): string => {
  switch (choice.kind) {
    case "top":
      return `Top ${choice.size}`
    case "bottom":
      return `Bottom ${choice.size}`
    case "middle":
      return "Middle"
    case "answer":
      return optionName(choice.label)
    case "net":
      return choice.name.trim()
    case "mean":
      return "Mean"
    case "nps":
      return "NPS"
    case "sum":
      return "Sum"
  }
}

/** Whether two choices show the same thing. */
export const sameSummary = (a: SummaryChoice, b: SummaryChoice): boolean => {
  if (a.kind !== b.kind) return false
  if ((a.kind === "top" || a.kind === "bottom") && (b.kind === "top" || b.kind === "bottom"))
    return a.size === b.size
  if (a.kind === "answer" && b.kind === "answer") return a.label === b.label
  if (a.kind === "net" && b.kind === "net")
    return a.name === b.name && a.members.join("\n") === b.members.join("\n")
  return true
}

/**
 * Whether `choice` suits `q`, scored `scores`: the one rule of what a
 * question is offered (the defaults' list, Add table, the inspector). A
 * number question takes the mean and the sum only, and the sum suits
 * nothing else; the boxes, the middle and the mean of answers need scores,
 * NPS scores of 0 to 10; one answer, and a net, need answers `q` has.
 */
export const summaryFits = (
  choice: SummaryChoice,
  q: Pick<CatalogueQuestion, "type" | "options">,
  scores: Record<string, number> | null
): boolean => {
  if (choice.kind === "sum") return isNumberQuestion(q)
  if (isNumberQuestion(q)) return choice.kind === "mean"
  const has = (label: string): boolean => q.options.some((o) => o.label === label)
  switch (choice.kind) {
    case "nps":
      return scores !== null && isNps(scores)
    case "answer":
      return has(choice.label)
    case "net":
      return choice.members.some(has)
    default:
      return scores !== null
  }
}

/** Whether `instance` is a row of a table fixed at `slice`: as many parts, each fixed part the same. */
export const inSlice = (
  instance: Pick<CatalogueInstance, "parts">,
  slice: (string | null)[] | undefined
): boolean =>
  !slice ||
  (instance.parts.length === slice.length &&
    slice.every((part, k) => part === null || part === instance.parts[k]))

/**
 * The slices a new summary of a question of several dimensions is made
 * with: its rows run down the grid rows (null where the catalogue put them,
 * `rowsDimension`), and every other dimension is fixed, one slice per
 * combination of their values in instance order. Rows by columns gives a
 * summary per column; a loop around a grid, one per iteration. [] for a
 * question of one dimension, and for one without grid rows (a question
 * asked in a loop, or in loops within loops, whatever its drivers are
 * called), which has one summary of every instance.
 */
export const defaultSlices = (
  q: Pick<CatalogueQuestion, "dimensions" | "instances" | "rowsDimension">
): (string | null)[][] => {
  const rows = q.rowsDimension ?? -1
  if (q.dimensions.length < 2 || rows < 0 || rows >= q.dimensions.length) return []
  const seen = new Set<string>()
  const out: (string | null)[][] = []
  q.instances.forEach((i) => {
    if (i.parts.length !== q.dimensions.length) return
    const slice = i.parts.map((part, k) => (k === rows ? null : part))
    const key = JSON.stringify(slice)
    if (seen.has(key)) return
    seen.add(key)
    out.push(slice)
  })
  return out
}

/** Dimension `k` of `q` as the screens name it: a loop by its driver question as shown, "Rows", "Columns". */
export const dimensionName = (
  q: Pick<CatalogueQuestion, "dimensions" | "rowsDimension">,
  k: number,
  names: ShownNames = RAW_NAMES
): string => {
  const dimension = q.dimensions[k] ?? ""
  return isGridDimension(q, k) ? dimension : names.variable(dimension)
}

/**
 * Part `part` of dimension `k` as the tables show it: a loop iteration by its
 * driver's shown label of that answer; a grid row or column as it is.
 */
export const partName = (
  q: Pick<CatalogueQuestion, "dimensions" | "rowsDimension">,
  k: number,
  part: string,
  names: ShownNames = RAW_NAMES
): string => {
  const dimension = q.dimensions[k]
  return dimension && !isGridDimension(q, k) ? names.option(dimension, part) : part
}

/** The loop drivers of `q`: the questions whose shown answers name its iterations (`partName`). */
export const loopDrivers = (q: Pick<CatalogueQuestion, "dimensions" | "rowsDimension">): string[] =>
  q.dimensions.filter((dimension, k) => dimension !== "" && !isGridDimension(q, k))

/** The values a table is fixed at, as shown, joined with " - ": "Online", "Alpha - Price". */
export const sliceWords = (
  q: Pick<CatalogueQuestion, "dimensions" | "rowsDimension">,
  slice: (string | null)[],
  names: ShownNames = RAW_NAMES
): string =>
  slice.flatMap((part, k) => (part === null ? [] : [partName(q, k, part, names)])).join(" - ")

/**
 * A row of a table fixed at `slice`, as the tables show it: the instance's
 * own shown label when it has one ("Price, web"), otherwise its parts where
 * the rows run, as shown ("Price", "Brand Alpha"). Without shown labels, its
 * raw parts.
 */
export const slicedRowName = (
  q: Pick<CatalogueQuestion, "label" | "dimensions" | "rowsDimension">,
  instance: Pick<CatalogueInstance, "id" | "label" | "parts">,
  slice: (string | null)[],
  names: ShownNames = RAW_NAMES
): string => {
  const own = names.instance(q.label, instance.id, instance.label)
  if (own !== instance.label) return own
  return slice
    .flatMap((part, k) => (part === null ? [partName(q, k, instance.parts[k] ?? "", names)] : []))
    .join(" - ")
}
